import type { Database } from "@nifgash/shared/db/types";
import { api, getSession, setSession } from "./client";
import { getInitData } from "../lib/telegram";

export type UserRow = Database["public"]["Tables"]["users"]["Row"];

type SessionResponse = { session: string; user: UserRow };

let currentUser: UserRow | null = null;

export function getSessionUser(): UserRow | null {
  return currentUser;
}

export function setSessionUser(user: UserRow) {
  currentUser = user;
}

/**
 * Resolve the current user from whichever auth source is available:
 *  1. existing JWT in localStorage (refresh case, both Mini App and website)
 *  2. Telegram initData (Mini App in-Telegram)
 *  3. otherwise: anonymous — caller renders the public landing + Login button
 */
export async function openSession(): Promise<UserRow | null> {
  if (getSession()) {
    try {
      const me = await api<{ user: UserRow }>("/api/me");
      currentUser = me.user;
      return me.user;
    } catch {
      // token expired or revoked — fall through to other auth paths
      setSession(null);
    }
  }

  const initData = getInitData();
  if (initData) {
    try {
      const res = await api<SessionResponse>("/api/session", {
        method: "POST",
        body: JSON.stringify({ initData }),
      });
      setSession(res.session);
      currentUser = res.user;
      return res.user;
    } catch {
      // Mini App auth failed — anonymous mode (e.g., expired auth_date)
    }
  }

  return null;
}

/** Exchange a Telegram Login Widget payload for a session JWT. */
export async function loginWithTelegram(payload: Record<string, unknown>): Promise<UserRow> {
  const res = await api<SessionResponse>("/api/auth/telegram", {
    method: "POST",
    body: JSON.stringify(payload),
  });
  setSession(res.session);
  currentUser = res.user;
  return res.user;
}

/** Exchange a Google Identity Services credential (id_token) for a session JWT. */
export async function loginWithGoogle(credential: string): Promise<UserRow> {
  const res = await api<SessionResponse>("/api/auth/google", {
    method: "POST",
    body: JSON.stringify({ credential }),
  });
  setSession(res.session);
  currentUser = res.user;
  return res.user;
}

export function logout() {
  setSession(null);
  currentUser = null;
}
